// V1 onboarding flow — full baseline + memory sequence game & feedback page
// Replicates baseline with 2 extra pages inserted after sitting-time (end of exercise block).
// Uses path: 'v1' so URLs are /onboarding/v1/{page-name}

import AlcoholPage from '../../baseline/onboarding/AlcoholPage'
import BiologicalSexPage from '../../baseline/onboarding/BiologicalSexPage'
import BirthYearPage from '../../baseline/onboarding/BirthYearPage'
import BloodGlucosePage from '../../baseline/onboarding/BloodGlucosePage'
import BloodPressurePage from '../../baseline/onboarding/BloodPressurePage'
import CancerScreeningsPage from '../../baseline/onboarding/CancerScreeningsPage'
import ChildrenPage from '../../baseline/onboarding/ChildrenPage'
import CholesterolPage from '../../baseline/onboarding/CholesterolPage'
import CountryPage from '../../baseline/onboarding/CountryPage'
import DoctorCheckupsPage from '../../baseline/onboarding/DoctorCheckupsPage'
import ExerciseFrequencyPage from '../../baseline/onboarding/ExerciseFrequencyPage'
import FruitsAndVeggiesPage from '../../baseline/onboarding/FruitsAndVeggiesPage'
import GrandparentsLifespanPage from '../../baseline/onboarding/GrandparentsLifespanPage'
import HabitsIntroPage from '../../baseline/onboarding/HabitsIntroPage'
import HealthcareIntroPage from '../../baseline/onboarding/HealthcareIntroPage'
import HeightPage from '../../baseline/onboarding/HeightPage'
import IncomeLevelPage from '../../baseline/onboarding/IncomeLevelPage'
import JobPage from '../../baseline/onboarding/JobPage'
import LivingLocationPage from '../../baseline/onboarding/LivingLocationPage'
import NutritionIntroPage from '../../baseline/onboarding/NutritionIntroPage'
import ProcessedFoodPage from '../../baseline/onboarding/ProcessedFoodPage'
import RelationshipStatusPage from '../../baseline/onboarding/RelationshipStatusPage'
import RunningDifficultyPage from '../../baseline/onboarding/RunningDifficultyPage'
import SittingTimePage from '../../baseline/onboarding/SittingTimePage'
import SleepHoursPage from '../../baseline/onboarding/SleepPage'
import SleepTroublePage from '../../baseline/onboarding/SleepTroublePage'
import SmokingPage from '../../baseline/onboarding/SmokingPage'
import SocialLifeIntroPage from '../../baseline/onboarding/SocialLifeIntroPage'
import SocialTimePage from '../../baseline/onboarding/SocialTimePage'
import StressPage from '../../baseline/onboarding/StressPage'
import SugarIntakePage from '../../baseline/onboarding/SugarIntakePage'
import SunProtectionPage from '../../baseline/onboarding/SunProtectionPage'
import WaterIntakePage from '../../baseline/onboarding/WaterIntakePage'
import WeightPage from '../../baseline/onboarding/WeightPage'

import MemoryGameFeedbackPage from './MemoryGameFeedbackPage'
import MemoryGamePage from './MemoryGamePage'

import navigateToNextOnboardingPageAction from '@actions/navigateToNextOnboardingPageAction'
import skipOnboardingLoader from '@app-studio-web/ui/loaders/skipOnboardingLoader'

import { Outlet } from 'react-router-dom'

const onboardingRoutes = [
  {
    path: 'v1',
    element: <Outlet />,
    loader: skipOnboardingLoader,
    children: [
      // OPENING (6 pages)
      {
        action: navigateToNextOnboardingPageAction,
        element: <BiologicalSexPage />,
        path: 'start',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <BiologicalSexPage />,
        path: 'biological-sex',
        handle: { skipInNavigation: true },
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <BirthYearPage />,
        path: 'birth-year',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <CountryPage />,
        path: 'country',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <WeightPage />,
        path: 'weight',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <HeightPage />,
        path: 'height',
      },

      // EXERCISE BLOCK (3 pages)
      {
        action: navigateToNextOnboardingPageAction,
        element: <ExerciseFrequencyPage />,
        path: 'exercise-frequency',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <RunningDifficultyPage />,
        path: 'running-difficulty',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <SittingTimePage />,
        path: 'sitting-time',
      },

      // V1-ONLY: Memory sequence game + feedback (inserted after exercise block)
      {
        action: navigateToNextOnboardingPageAction,
        element: <MemoryGamePage />,
        path: 'memory-game',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <MemoryGameFeedbackPage />,
        path: 'memory-feedback',
      },

      // HABITS BLOCK (6 pages)
      {
        action: navigateToNextOnboardingPageAction,
        element: <HabitsIntroPage />,
        path: 'habits-intro',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <SmokingPage />,
        path: 'smoking',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <AlcoholPage />,
        path: 'alcohol',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <SleepHoursPage />,
        path: 'sleep',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <StressPage />,
        path: 'stress',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <SleepTroublePage />,
        path: 'sleep-trouble',
      },

      // NUTRITION BLOCK (5 pages)
      {
        action: navigateToNextOnboardingPageAction,
        element: <NutritionIntroPage />,
        path: 'nutrition-intro',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <WaterIntakePage />,
        path: 'water-intake',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <SugarIntakePage />,
        path: 'sugar-intake',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ProcessedFoodPage />,
        path: 'processed-food',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <FruitsAndVeggiesPage />,
        path: 'fruits-and-veggies',
      },

      // HEALTHCARE BLOCK (8 pages)
      {
        action: navigateToNextOnboardingPageAction,
        element: <HealthcareIntroPage />,
        path: 'healthcare-intro',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <GrandparentsLifespanPage />,
        path: 'grandparents-lifespan',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <DoctorCheckupsPage />,
        path: 'doctor-checkups',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <CancerScreeningsPage />,
        path: 'cancer-screenings',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <BloodPressurePage />,
        path: 'blood-pressure',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <CholesterolPage />,
        path: 'cholesterol',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <BloodGlucosePage />,
        path: 'blood-glucose',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <SunProtectionPage />,
        path: 'sun-protection',
      },

      // SOCIAL BLOCK (7 pages)
      {
        action: navigateToNextOnboardingPageAction,
        element: <SocialLifeIntroPage />,
        path: 'social-life-intro',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <IncomeLevelPage />,
        path: 'income-level',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <JobPage />,
        path: 'job',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <LivingLocationPage />,
        path: 'living-location',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <RelationshipStatusPage />,
        path: 'relationship-status',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <SocialTimePage />,
        path: 'social-time',
      },
      {
        action: navigateToNextOnboardingPageAction,
        element: <ChildrenPage />,
        path: 'children',
      },
    ],
  },
]

export default onboardingRoutes
